import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { bomApi, type BomItemView } from "../../api/structure";
import type { ApiError } from "../../api/client";
import { detailsMap, formatApiError } from "../../lib/apiError";
import { formatQuantity, parseQuantity } from "../../lib/quantity";
import { ArticleCombobox, articleRef, type ArticleRef } from "../../components/ArticleCombobox";
import { ArticleFormModal } from "../../components/ArticleFormModal";
import { Modal } from "../../components/Modal";
import { errorBox, textarea } from "./shared";

export type BomItemModalMode = { kind: "add" } | { kind: "edit"; item: BomItemView };

/**
 * One line of a position's sastavnica (sprint 5l §4, §7): add = article from the catalogue
 * (searchable; "Nov artikal" opens the article form in place of this dialog and picks what it
 * made) + quantity in the article's unit + note; edit = quantity and note of an existing line
 * (the article never changes — another article is another line). A 409 ARTICLE_TAKEN offers
 * the edit of the line that already carries the article.
 */
export function BomItemModal({
  positionId, mode, canCreateArticle, onClose, onSaved, onEditExisting,
}: {
  positionId: string;
  mode: BomItemModalMode;
  /** `articles:create`: shows "Nov artikal" in the article picker. */
  canCreateArticle: boolean;
  onClose: () => void;
  onSaved: (item: BomItemView, created: boolean) => void;
  /** 409 ARTICLE_TAKEN: the caller swaps this dialog for the edit of `itemId`. */
  onEditExisting: (itemId: string) => void;
}) {
  const { t } = useTranslation();
  const editing = mode.kind === "edit" ? mode.item : null;
  const [article, setArticle] = useState<ArticleRef | null>(null);
  const [quantity, setQuantity] = useState(editing ? formatQuantity(editing.quantity) : "");
  const [note, setNote] = useState(editing?.note ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quantityError, setQuantityError] = useState<string | null>(null);
  const [articleError, setArticleError] = useState<string | null>(null);
  const [takenItemId, setTakenItemId] = useState<string | null>(null);
  const [articleForm, setArticleForm] = useState(false);

  const unit = editing ? editing.unit : (article?.unit ?? null);
  const parsed = parseQuantity(quantity);
  const quantityText = t("bom.error.quantity", "Količina mora biti broj veći od 0, sa najviše tri decimale.");
  const quantityInvalid = quantityError !== null || (quantity.trim() !== "" && parsed === null);

  // Every disabled control says why (2026-10-08): the submit button carries the first obstacle.
  const submitReason = submitting
    ? t("action.saving", "Čuvanje…")
    : !editing && !article
      ? t("bom.error.article", "Izaberite artikal.")
      : quantity.trim() === ""
        ? t("bom.error.quantityMissing", "Unesite količinu.")
        : parsed === null
          ? quantityText
          : null;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitReason !== null || parsed === null) return;
    setError(null);
    setTakenItemId(null);
    setArticleError(null);
    setQuantityError(null);
    setSubmitting(true);
    try {
      if (editing) {
        onSaved(await bomApi.update(positionId, editing.id, { quantity: parsed, note: note.trim() || null }), false);
      } else if (article) {
        onSaved(await bomApi.add(positionId, { articleId: article.id, quantity: parsed, note: note.trim() || null }), true);
      }
    } catch (err) {
      const ae = err as ApiError;
      const d = detailsMap(ae);
      if (ae.code === "ARTICLE_TAKEN") {
        setTakenItemId(d?.itemId ? String(d.itemId) : null);
        setError(t("bom.error.duplicate", "Artikal je već na sastavnici ove pozicije."));
      } else if (ae.code === "VALIDATION" && d?.articleId) {
        setArticleError(t("bom.error.articleNotFound", "Izabrani artikal više ne postoji — izaberite drugi."));
      } else if (ae.code === "VALIDATION" && d?.quantity) {
        setQuantityError(quantityText);
      } else if (ae.status === 404) {
        setError(t("bom.error.positionGone", "Pozicija je u međuvremenu obrisana — sastavnica obrisane pozicije se ne menja."));
      } else {
        setError(formatApiError(ae));
      }
    } finally {
      setSubmitting(false);
    }
  }

  // "Nov artikal": the article form takes the dialog's place (no stacked modals); what it makes is picked.
  if (articleForm) {
    return (
      <ArticleFormModal
        onClose={() => setArticleForm(false)}
        onSaved={(a) => { setArticle(articleRef(a)); setArticleError(null); setArticleForm(false); }}
      />
    );
  }

  return (
    <Modal
      title={editing ? t("bom.modal.editTitle", "Izmena stavke sastavnice") : t("bom.modal.createTitle", "Nova stavka sastavnice")}
      subtitle={editing
        ? <span><span style={{ fontFamily: "var(--mono)" }}>{editing.articleCode}</span>{editing.articleName ? ` — ${editing.articleName}` : ""}</span>
        : t("bom.modal.createSub", "Izaberite artikal iz kataloga i unesite količinu u njegovoj jedinici.")}
      onClose={onClose}
      width={560}
    >
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {!editing && (
          <div className="field">
            <span>{t("bom.field.article", "Artikal")} <span className="req-mark">*</span></span>
            <ArticleCombobox
              value={article}
              onChange={(a) => { setArticle(a); setArticleError(null); setTakenItemId(null); setError(null); }}
              autoFocus
              onCreateNew={canCreateArticle ? () => setArticleForm(true) : undefined}
            />
            {articleError && <small className="field-error" role="alert">{articleError}</small>}
            {article && (
              <span className="muted" style={{ fontSize: 12 }}>
                {article.typeName} · {t("bom.unitOf", "jedinica: {{unit}}", { unit: article.unit })}
              </span>
            )}
          </div>
        )}
        <label className="field">
          <span>{t("bom.field.quantity", "Količina")} <span className="req-mark">*</span></span>
          <div className="bom-qty">
            <input
              className={quantityInvalid ? "input is-invalid" : "input"}
              type="text"
              inputMode="decimal"
              value={quantity}
              onChange={(e) => { setQuantity(e.target.value); setQuantityError(null); }}
              placeholder={t("bom.field.quantityPlaceholder", "npr. 2,5")}
              autoFocus={!!editing}
              autoComplete="off"
            />
            <span className="bom-qty-unit" title={t("bom.field.unit", "Jedinica artikla")}>{unit ?? "—"}</span>
          </div>
          {quantityInvalid
            ? <small className="field-error" role="alert">{quantityError ?? quantityText}</small>
            : <small className="muted" style={{ fontSize: 12 }}>{t("bom.quantityHint", "Broj veći od 0, decimale sa zarezom ili tačkom (najviše tri), u jedinici artikla.")}</small>}
        </label>
        <label className="field">
          <span>{t("bom.field.note", "Napomena")}</span>
          <textarea className="input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} style={textarea} />
        </label>
        {error && (
          <div style={errorBox} role="alert">
            {error}
            {takenItemId && (
              <div style={{ marginTop: 8 }}>
                <button type="button" className="btn sm" onClick={() => onEditExisting(takenItemId)}>
                  {t("bom.error.duplicateEdit", "Izmeni postojeću stavku")}
                </button>
              </div>
            )}
          </div>
        )}
        <div className="form-actions">
          <button type="button" className="btn" onClick={onClose}>{t("action.cancel", "Otkaži")}</button>
          <span className="tip-wrap" data-tip={submitReason ?? undefined} title={submitReason ?? undefined}>
            <button type="submit" className="btn primary" disabled={submitReason !== null}>
              {submitting ? t("action.saving", "Čuvanje…") : editing ? t("action.save", "Sačuvaj") : t("action.add", "Dodaj")}
            </button>
          </span>
        </div>
      </form>
    </Modal>
  );
}
